/** Version-one feet-anchored platformer motion. No clock, RNG, input polling or
 * render state: hosts supply one fixed step and authoritative ordered solids.
 * This is the running/jumping profile, not the entire motion.body2d catalog. */
export interface PlatformerBodyV1 {
  x: number; y: number; vx: number; vy: number; facing: -1 | 1;
  grounded: boolean; airborne: number; jumpHeld: boolean;
}
export interface PlatformerSolidV1 { x: number; y: number; w: number; h: number }
export interface PlatformerMotionConfigV1 {
  dt: number; runSpeed: number; gravity: number; jumpSpeed: number;
  maxFallSpeed: number; jumpCut: number; coyoteTicks: number;
  width: number; height: number; worldWidth: number;
}

export function createPlatformerMotionV1(config: PlatformerMotionConfigV1) {
  const c = Object.freeze({ ...config });
  for (const key of ['dt', 'runSpeed', 'jumpSpeed', 'maxFallSpeed', 'width', 'height', 'worldWidth'] as const) {
    if (!Number.isFinite(c[key]) || c[key] <= 0) throw new RangeError(`platformer ${key} must be positive and finite`);
  }
  if (!Number.isFinite(c.gravity) || c.gravity < 0 || !Number.isFinite(c.jumpCut) || c.jumpCut < 0 || c.jumpCut > 1
    || !Number.isSafeInteger(c.coyoteTicks) || c.coyoteTicks < 0 || c.worldWidth < c.width) {
    throw new RangeError('invalid platformer gravity, jump cut, coyote window or world bounds');
  }
  return (body: Readonly<PlatformerBodyV1>, input: { dx: -1 | 0 | 1; jump: boolean }, solids: readonly PlatformerSolidV1[]) => {
    const next = { ...body };
    next.vx = input.dx * c.runSpeed;
    if (input.dx !== 0) next.facing = input.dx > 0 ? 1 : -1;
    const jumped = input.jump && !next.jumpHeld && (next.grounded || next.airborne <= c.coyoteTicks);
    if (jumped) {
      next.vy = -c.jumpSpeed;
      next.grounded = false;
      next.airborne = c.coyoteTicks + 1;
    }
    if (!input.jump && next.jumpHeld && next.vy < 0) next.vy *= c.jumpCut;
    next.jumpHeld = input.jump;
    next.vy = Math.min(next.vy + c.gravity * c.dt, c.maxFallSpeed);

    // Resolve axes separately: resolving both together snags floor seams.
    const overlaps = (s: PlatformerSolidV1) => next.x - c.width / 2 < s.x + s.w
      && next.x + c.width / 2 > s.x && next.y - c.height < s.y + s.h && next.y > s.y;
    next.x = Math.max(c.width / 2, Math.min(c.worldWidth - c.width / 2, next.x + next.vx * c.dt));
    for (const solid of solids) {
      if (!overlaps(solid)) continue;
      if (next.vx > 0) next.x = solid.x - c.width / 2;
      else if (next.vx < 0) next.x = solid.x + solid.w + c.width / 2;
      next.vx = 0;
    }
    next.y += next.vy * c.dt;
    const wasGrounded = next.grounded;
    next.grounded = false;
    for (const solid of solids) {
      if (!overlaps(solid)) continue;
      if (next.vy > 0) {
        next.y = solid.y;
        next.vy = 0;
        next.grounded = true;
      } else if (next.vy < 0) {
        next.y = solid.y + solid.h + c.height;
        next.vy = 0;
      }
    }
    next.airborne = next.grounded ? 0 : next.airborne + 1;
    return { body: next, jumped, landed: next.grounded && !wasGrounded };
  };
}

/** A named platform surface for V2. Coordinates retain the V1 convention:
 * x/y is the upper-left corner and a body position is its feet centre. */
export interface PlatformerSurfaceV2 extends PlatformerSolidV1 {
  id: string;
  mode: 'solid' | 'oneWayTop';
}

export interface PlatformerContactV2 {
  solidId: string;
  /** Points from the contacted surface towards the body. */
  normal: { x: -1 | 0 | 1; y: -1 | 0 | 1 };
  side: 'left' | 'right' | 'top' | 'bottom';
}

export interface PlatformerMotionResultV2 {
  body: PlatformerBodyV1;
  jumped: boolean;
  landed: boolean;
  /** Stable by surface id, then contact side. It is an observation, not an
   * impulse/physics event stream. */
  contacts: readonly PlatformerContactV2[];
}

/** Additive contact-aware platform motion. V1 deliberately remains unchanged
 * for existing games. One-way platforms accept only a descending crossing of
 * their top face; they never block a jump, side movement, or an already-below
 * body. */
export function createPlatformerMotionV2(config: PlatformerMotionConfigV1) {
  const c = Object.freeze({ ...config });
  for (const key of ['dt', 'runSpeed', 'jumpSpeed', 'maxFallSpeed', 'width', 'height', 'worldWidth'] as const) {
    if (!Number.isFinite(c[key]) || c[key] <= 0) throw new RangeError(`platformer ${key} must be positive and finite`);
  }
  if (!Number.isFinite(c.gravity) || c.gravity < 0 || !Number.isFinite(c.jumpCut) || c.jumpCut < 0 || c.jumpCut > 1
    || !Number.isSafeInteger(c.coyoteTicks) || c.coyoteTicks < 0 || c.worldWidth < c.width) {
    throw new RangeError('invalid platformer gravity, jump cut, coyote window or world bounds');
  }
  return (body: Readonly<PlatformerBodyV1>, input: { dx: -1 | 0 | 1; jump: boolean }, surfaces: readonly PlatformerSurfaceV2[]): PlatformerMotionResultV2 => {
    const ids = new Set<string>();
    for (const surface of surfaces) {
      if (!surface.id || ids.has(surface.id) || (surface.mode !== 'solid' && surface.mode !== 'oneWayTop')
        || !Number.isFinite(surface.x) || !Number.isFinite(surface.y) || !Number.isFinite(surface.w) || !Number.isFinite(surface.h)
        || surface.w <= 0 || surface.h <= 0) throw new RangeError('platformer surfaces require unique IDs, a supported mode and finite positive bounds');
      ids.add(surface.id);
    }
    const next = { ...body };
    const contacts: PlatformerContactV2[] = [];
    const contact = (surface: PlatformerSurfaceV2, side: PlatformerContactV2['side'], normal: PlatformerContactV2['normal']) => {
      contacts.push({ solidId: surface.id, side, normal });
    };
    const overlaps = (surface: PlatformerSurfaceV2) => next.x - c.width / 2 < surface.x + surface.w
      && next.x + c.width / 2 > surface.x && next.y - c.height < surface.y + surface.h && next.y > surface.y;

    next.vx = input.dx * c.runSpeed;
    if (input.dx !== 0) next.facing = input.dx > 0 ? 1 : -1;
    const jumped = input.jump && !next.jumpHeld && (next.grounded || next.airborne <= c.coyoteTicks);
    if (jumped) {
      next.vy = -c.jumpSpeed;
      next.grounded = false;
      next.airborne = c.coyoteTicks + 1;
    }
    if (!input.jump && next.jumpHeld && next.vy < 0) next.vy *= c.jumpCut;
    next.jumpHeld = input.jump;
    next.vy = Math.min(next.vy + c.gravity * c.dt, c.maxFallSpeed);

    next.x = Math.max(c.width / 2, Math.min(c.worldWidth - c.width / 2, next.x + next.vx * c.dt));
    for (const surface of surfaces) {
      if (surface.mode !== 'solid' || !overlaps(surface)) continue;
      if (next.vx > 0) { next.x = surface.x - c.width / 2; contact(surface, 'left', { x: -1, y: 0 }); }
      else if (next.vx < 0) { next.x = surface.x + surface.w + c.width / 2; contact(surface, 'right', { x: 1, y: 0 }); }
      else continue;
      next.vx = 0;
    }

    const previousFeet = next.y;
    next.y += next.vy * c.dt;
    const wasGrounded = next.grounded;
    next.grounded = false;
    for (const surface of surfaces) {
      if (surface.mode === 'oneWayTop') {
        const crossesTop = next.vy > 0 && previousFeet <= surface.y && next.y >= surface.y
          && next.x + c.width / 2 > surface.x && next.x - c.width / 2 < surface.x + surface.w;
        if (!crossesTop) continue;
        next.y = surface.y; next.vy = 0; next.grounded = true;
        contact(surface, 'top', { x: 0, y: -1 });
        continue;
      }
      if (!overlaps(surface)) continue;
      if (next.vy > 0) {
        next.y = surface.y; next.vy = 0; next.grounded = true;
        contact(surface, 'top', { x: 0, y: -1 });
      } else if (next.vy < 0) {
        next.y = surface.y + surface.h + c.height; next.vy = 0;
        contact(surface, 'bottom', { x: 0, y: 1 });
      }
    }
    next.airborne = next.grounded ? 0 : next.airborne + 1;
    contacts.sort((a, b) => a.solidId === b.solidId
      ? (a.side === b.side ? 0 : a.side < b.side ? -1 : 1)
      : a.solidId < b.solidId ? -1 : 1);
    return { body: next, jumped, landed: next.grounded && !wasGrounded, contacts };
  };
}
